import Box from '@material-ui/core/Box';
import CustomSnackBar from '../../audienceManager/customSnackBar';
import GhostLoader from 'react-ghost-loader';
import Grid from '@material-ui/core/Grid';
import React, { useEffect, useMemo } from 'react';
import Remodeling from '../remodeling';
import SegmentDetailCharts from '../segmentDetailCharts';
import SegmentDetailPercentileCards from '../segmentDetailPercentileCards';
import SegmentDetailStatCards from '../segmentDetailStatCards';
import Tabs from '../../../components/Tabs/Tabs';
import Tooltip from '@material-ui/core/Tooltip';
import useSegmentExports from '../../../graphql/hooks/useSegmentExports';
import { ArrowLeft, Table } from '@phosphor-icons/react';
import { Button } from '@attain-sre/attain-design-system';
import { GET_TTD_OTS_SEGMENT_DETAIL_BY_ID } from '../../../graphql/queries';
import { KeyboardDoubleArrowRightOutlined } from '@mui/icons-material';
import {
  NO_DATA,
  OTS_SGM_MNG_EDIT_AUDIENCE,
  OTS_SGM_MNG_NO_AUDIENCE_TOOLTIP,
  OTS_SGM_MNG_NO_DESCRIPTION_AVAILABLE,
  OTS_SGM_MNG_NO_RECORD_FOUND,
  OTS_SGM_MNG_REMODELING_DISABLED_TOOLTIP,
  OTS_SGM_MNG_SEGMENT_NOT_FOUND_MESSAGE,
  OTS_SGM_MNG_TAB_OVERVIEW,
  OTS_SGM_MNG_TAB_REMODELING,
} from '../../../content';
import { RateLevel, Routes, SegmentDetailTabs } from '../../../constants';
import { removeNotification } from '../slice';
import { SegmentDetail as SegmentDetailType } from '../types';
import {
  buildSegmentQueryVariables,
  parseAndValidateSegmentParams,
} from '../utils/urlHelpers';
import { buildSegmentDetailUrl, getEffectiveAudienceId } from '../utils/utils';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import * as Styled from './index.styles';

const SegmentDetail: React.FC = () => {
  const { segmentId, platform } = useParams<{
    platform: string;
    segmentId: string;
  }>();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Get notifications from Redux state
  const notifications = useAppSelector(
    (state) => state.segmentManagerReducer?.ui?.notifications || []
  );

  // Parse and validate URL parameters
  const urlValidation = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return parseAndValidateSegmentParams(searchParams, segmentId);
  }, [location.search, segmentId]);

  // Build GraphQL variables using helper (always call hooks before early returns)
  const queryVariables = useMemo(() => {
    if (!urlValidation.isValid || !urlValidation.params) return null;
    const { rateLevel, advertiserId, partnerId } = urlValidation.params;
    return buildSegmentQueryVariables(
      segmentId,
      rateLevel,
      advertiserId || undefined,
      partnerId || undefined
    );
  }, [urlValidation, segmentId]);

  const { data, error, loading } = useQuery<{
    ttdOtsSegmentDetailById: SegmentDetailType;
  }>(GET_TTD_OTS_SEGMENT_DETAIL_BY_ID, {
    variables: queryVariables || {},
    skip: !queryVariables,
    fetchPolicy: 'network-only',
  });

  // Fetch segment exports data upfront to validate Remodeling tab availability
  const {
    loading: exportsLoading,
    error: exportsError,
    segmentExports,
  } = useSegmentExports({
    segmentId,
    rateLevel: urlValidation.params?.rateLevel || RateLevel.SYSTEM,
  });

  // Determine if remodeling should be enabled
  const isRemodelingEnabled = useMemo(() => {
    // Disabled if loading or error
    if (exportsLoading || exportsError) return false;

    // Enabled only if OTS segment exports exist
    return segmentExports && segmentExports.length > 0;
  }, [exportsLoading, exportsError, segmentExports]);

  // Determine active tab based on URL path (must be before early returns)
  const isRemodelingTab = location.pathname.includes(
    `/${SegmentDetailTabs.REMODELING}`
  );
  const activeTab = isRemodelingTab
    ? SegmentDetailTabs.REMODELING
    : SegmentDetailTabs.OVERVIEW;

  // Redirect to Overview if on Remodeling tab but it becomes disabled
  useEffect(() => {
    if (
      activeTab === SegmentDetailTabs.REMODELING &&
      !isRemodelingEnabled &&
      !exportsLoading &&
      urlValidation.isValid
    ) {
      const queryString = new URLSearchParams(location.search).toString();
      const url = buildSegmentDetailUrl({
        platform,
        segmentId,
        queryString,
      });
      history.replace(url);
    }
  }, [
    isRemodelingEnabled,
    activeTab,
    exportsLoading,
    urlValidation.isValid,
    history,
    location.search,
    platform,
    segmentId,
  ]);

  // If validation fails, show error page
  if (!urlValidation.isValid) {
    return (
      <Box>
        <Styled.HeaderContainer>
          <Styled.HeaderLeft>
            <Styled.BackButton
              onClick={() => history.push(Routes.SEGMENT_MANAGER)}
            >
              <ArrowLeft size={20} />
            </Styled.BackButton>
          </Styled.HeaderLeft>
        </Styled.HeaderContainer>
        <Styled.NoRecordContainer>
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <Table size={48} color="rgb(128, 128, 128)" />
            </Grid>
            <Grid item>
              <Styled.NoRecordText>Invalid URL Parameters</Styled.NoRecordText>
            </Grid>
            <Grid item>
              <Styled.NoRecordSubText>
                {urlValidation.error}
              </Styled.NoRecordSubText>
            </Grid>
          </Grid>
        </Styled.NoRecordContainer>
      </Box>
    );
  }

  // At this point, validation is valid and params are defined
  const { rateLevel, advertiserId, partnerId } = urlValidation.params || {
    rateLevel: RateLevel.SYSTEM,
    advertiserId: undefined,
    partnerId: undefined,
  };

  const segmentDetail = data?.ttdOtsSegmentDetailById;

  const handleBackClick = () => {
    history.push(Routes.SEGMENT_MANAGER);
  };

  const effectiveAudienceId = getEffectiveAudienceId(activeTab, segmentDetail);

  const handleEditAudience = () => {
    if (effectiveAudienceId) {
      const audienceEditorUrl = `${Routes.AUDIENCE_STUDIO_EDITOR}/${effectiveAudienceId}`;
      window.open(audienceEditorUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleTabChange = (tab: SegmentDetailTabs) => {
    // Prevent navigation to disabled Remodeling tab
    if (tab === SegmentDetailTabs.REMODELING && !isRemodelingEnabled) {
      return;
    }

    const queryString = new URLSearchParams(location.search).toString();
    const url = buildSegmentDetailUrl({
      platform,
      segmentId,
      queryString,
      tab: tab === SegmentDetailTabs.REMODELING ? tab : undefined,
    });

    history.push(url);
  };

  if (error || (!loading && !segmentDetail)) {
    return (
      <Box>
        <Styled.HeaderContainer>
          <Styled.HeaderLeft>
            <Styled.BackButton onClick={handleBackClick}>
              <ArrowLeft size={20} />
            </Styled.BackButton>
          </Styled.HeaderLeft>
        </Styled.HeaderContainer>
        <Styled.NoRecordContainer>
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <Table size={48} color="rgb(128, 128, 128)" />
            </Grid>
            <Grid item>
              <Styled.NoRecordText>
                {OTS_SGM_MNG_NO_RECORD_FOUND}
              </Styled.NoRecordText>
            </Grid>
            <Grid item>
              <Styled.NoRecordSubText>
                {OTS_SGM_MNG_SEGMENT_NOT_FOUND_MESSAGE}
              </Styled.NoRecordSubText>
            </Grid>
          </Grid>
        </Styled.NoRecordContainer>
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Styled.HeaderContainer>
          <Styled.HeaderLeft>
            <Styled.BackButtonRow>
              <Styled.BackButton onClick={handleBackClick}>
                <ArrowLeft size={26} />
              </Styled.BackButton>
              {loading ? (
                <Styled.SegmentNamePlaceholder>
                  <GhostLoader height="30px" width="500px" />
                </Styled.SegmentNamePlaceholder>
              ) : (
                <Styled.SegmentName>
                  {segmentDetail?.segmentName || NO_DATA}
                </Styled.SegmentName>
              )}
            </Styled.BackButtonRow>
            {loading ? (
              <Styled.DescriptionPlaceholder>
                <GhostLoader height="20px" width="800px" />
              </Styled.DescriptionPlaceholder>
            ) : (
              <Styled.Description>
                {segmentDetail?.description ||
                  OTS_SGM_MNG_NO_DESCRIPTION_AVAILABLE}
              </Styled.Description>
            )}
          </Styled.HeaderLeft>
          <Styled.HeaderRight>
            <Tooltip
              title={
                !loading && !effectiveAudienceId
                  ? OTS_SGM_MNG_NO_AUDIENCE_TOOLTIP
                  : ''
              }
              arrow
              placement="bottom"
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleEditAudience}
                  disabled={loading || !effectiveAudienceId}
                  endIcon={
                    <KeyboardDoubleArrowRightOutlined
                      style={{ fontSize: '24px' }}
                    />
                  }
                  style={{ fontSize: '16px' }}
                >
                  {OTS_SGM_MNG_EDIT_AUDIENCE}
                </Button>
              </span>
            </Tooltip>
          </Styled.HeaderRight>
        </Styled.HeaderContainer>
        <Styled.TabsContainer>
          <Tabs
            tabs={[
              {
                title: OTS_SGM_MNG_TAB_OVERVIEW,
                active: activeTab === SegmentDetailTabs.OVERVIEW,
                onClick: () => handleTabChange(SegmentDetailTabs.OVERVIEW),
              },
              {
                title: OTS_SGM_MNG_TAB_REMODELING,
                active: activeTab === SegmentDetailTabs.REMODELING,
                onClick: () => handleTabChange(SegmentDetailTabs.REMODELING),
                disabled: !isRemodelingEnabled,
                element: !isRemodelingEnabled ? (
                  <Tooltip
                    title={OTS_SGM_MNG_REMODELING_DISABLED_TOOLTIP}
                    arrow
                    placement="bottom"
                  >
                    <span>{OTS_SGM_MNG_TAB_REMODELING}</span>
                  </Tooltip>
                ) : undefined,
              },
            ]}
          />
        </Styled.TabsContainer>
        {activeTab === SegmentDetailTabs.OVERVIEW ? (
          <Styled.ContentContainer>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SegmentDetailPercentileCards
                  segmentDetail={segmentDetail}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <SegmentDetailStatCards
                  segmentDetail={segmentDetail}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <SegmentDetailCharts
                  segmentId={segmentId}
                  rateLevel={rateLevel}
                  advertiserId={advertiserId || undefined}
                  partnerId={partnerId || undefined}
                />
              </Grid>
            </Grid>
          </Styled.ContentContainer>
        ) : (
          <Remodeling
            segmentId={segmentId}
            segmentExports={segmentExports}
            exportsLoading={exportsLoading}
            exportsError={exportsError}
          />
        )}
      </Box>
      {notifications.map(({ state, message }, i) => (
        <CustomSnackBar
          index={i}
          trigger={true}
          severity={
            state === 'done'
              ? 'success'
              : state === 'warning'
              ? 'warning'
              : 'error'
          }
          message={message || ''}
          key={i}
          removeNotificationFunction={() => dispatch(removeNotification(i))}
        />
      ))}
    </>
  );
};

export default SegmentDetail;
